import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

interface PartnerBranding {
  id: string;
  partner_name: string;
  subdomain_prefix: string;
  logo_url: string | null;
  primary_color: string | null;
  welcome_message: string | null;
  contact_email: string | null;
  phone: string | null;
  website: string | null;
  status: string;
  subdomain_enabled: boolean;
}

interface PartnerBrandingContextType {
  partner: PartnerBranding | null;
  loading: boolean;
  isPartnerContext: boolean;
  partnerSlug: string | null;
}

const PartnerBrandingContext = createContext<PartnerBrandingContextType>({
  partner: null,
  loading: true,
  isPartnerContext: false,
  partnerSlug: null,
});

// Reserved subdomains that cannot be used
const RESERVED_SUBDOMAINS = ['admin', 'api', 'www', 'app', 'mail', 'partner', 'partners', 'support', 'help'];

export function PartnerBrandingProvider({ children }: { children: ReactNode }) {
  const [partner, setPartner] = useState<PartnerBranding | null>(null);
  const [loading, setLoading] = useState(true);
  const [partnerSlug, setPartnerSlug] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    const detectPartner = async () => {
      setLoading(true);
      
      // Method 1: Check subdomain from host
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      let detectedSlug: string | null = null;
      
      // Check for subdomain pattern: {partner}.livingtribute.co or {partner}.localhost
      if (parts.length >= 2) {
        const potentialSlug = parts[0].toLowerCase();
        // Exclude reserved subdomains and common patterns
        if (!RESERVED_SUBDOMAINS.includes(potentialSlug) && 
            potentialSlug !== 'localhost' && 
            !potentialSlug.includes('lovable') &&
            !potentialSlug.match(/^[a-f0-9-]{36}$/)) { // Exclude UUIDs
          detectedSlug = potentialSlug;
        }
      }
      
      // Method 2: Fallback to query parameter
      if (!detectedSlug) {
        detectedSlug = searchParams.get('partner');
      }
      
      // Method 3: Check localStorage for persisted partner (for attribution)
      if (!detectedSlug) {
        const storedPartner = localStorage.getItem('partner_attribution');
        if (storedPartner) {
          try {
            const parsed = JSON.parse(storedPartner);
            // Only use if less than 30 days old
            if (parsed.timestamp && Date.now() - parsed.timestamp < 30 * 24 * 60 * 60 * 1000) {
              detectedSlug = parsed.slug;
            } else {
              localStorage.removeItem('partner_attribution');
            }
          } catch {
            localStorage.removeItem('partner_attribution');
          }
        }
      }
      
      if (!detectedSlug) {
        setPartner(null);
        setPartnerSlug(null);
        setLoading(false);
        return;
      }
      
      setPartnerSlug(detectedSlug);
      
      // Fetch partner data
      try {
        const { data, error } = await supabase
          .from('partner_subdomains')
          .select('id, partner_name, subdomain_prefix, logo_url, primary_color, welcome_message, contact_email, phone, website, status, subdomain_enabled')
          .eq('subdomain_prefix', detectedSlug.toLowerCase())
          .maybeSingle();
        
        if (error) {
          console.error('Error fetching partner:', error);
          setPartner(null);
        } else if (data) {
          // Check if partner is active and subdomain is enabled
          if (data.status === 'active' && data.subdomain_enabled) {
            setPartner(data);
            
            // Store attribution for later
            localStorage.setItem('partner_attribution', JSON.stringify({
              slug: detectedSlug,
              partnerId: data.id,
              timestamp: Date.now(),
            }));
          } else if (data.status === 'suspended') {
            // Partner is suspended - we'll show a message but still set the partner
            setPartner({ ...data, subdomain_enabled: false });
          } else {
            setPartner(null);
          }
        } else {
          setPartner(null);
        }
      } catch (error) {
        console.error('Error fetching partner:', error);
        setPartner(null);
      }
      
      setLoading(false);
    };
    
    detectPartner();
  }, [searchParams, location.pathname]);

  const isPartnerContext = !!partner && partner.status === 'active' && partner.subdomain_enabled;

  return (
    <PartnerBrandingContext.Provider value={{ partner, loading, isPartnerContext, partnerSlug }}>
      {children}
    </PartnerBrandingContext.Provider>
  );
}

export function usePartnerBranding() {
  return useContext(PartnerBrandingContext);
}

// Helper to get partner-branded colors as CSS variables
export function getPartnerColorStyles(primaryColor: string | null) {
  if (!primaryColor) return {};
  
  // Parse hex color to HSL for theming
  try {
    const hex = primaryColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16) / 255;
    const g = parseInt(hex.substr(2, 2), 16) / 255;
    const b = parseInt(hex.substr(4, 2), 16) / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }
    
    const hue = Math.round(h * 360);
    const sat = Math.round(s * 100);
    const light = Math.round(l * 100);
    const hslValue = `${hue} ${sat}% ${light}%`;
    
    // Calculate a lighter variant for accent (increase lightness)
    const accentLight = Math.min(light + 10, 90);
    const accentHsl = `${hue} ${sat}% ${accentLight}%`;
    
    // Override the design system variables
    return {
      '--primary': hslValue,
      '--primary-foreground': light > 50 ? '0 0% 10%' : '0 0% 98%',
      '--accent': accentHsl,
      '--accent-foreground': accentLight > 50 ? '0 0% 10%' : '0 0% 98%',
    } as React.CSSProperties;
  } catch {
    return {};
  }
}
